import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DocsService } from '@shared/services/docs.service';
import { PrintService } from '@shared/services/print.service';
import { AutoPrintSwitch, DataSourceTypes, IPrintConfiguration } from '@shared/services/terminalAdministration.service';
import { Subject, takeUntil } from 'rxjs';

function normalizeAlias(value: unknown): string {
	return (value as string)?.trim().toLowerCase() ?? '';
}

function extractSelfId(self: string): string {
	return self.split('/').pop() ?? self;
}

function uniqueInArray(getArray: () => FormArray<FormGroup>, extract: (fg: FormGroup) => unknown, excludeIndex?: () => number): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = normalizeAlias(control.value);
		if (!value) return null;
		const taken = getArray().controls.some((fg, idx) => {
			if (excludeIndex && idx === excludeIndex()) return false;
			return normalizeAlias(extract(fg)) === value;
		});
		return taken ? { aliasNotUnique: true } : null;
	};
}

export function buildTerminalConfigsPayload(controls: FormGroup[]): IPrintConfiguration[] {
	return controls.map((control) => {
		const raw = control.getRawValue() as {
			alias: string;
			description: string;
			autoPrint: boolean;
			dataSource: DataSourceTypes;
			printer: string;
			template: string;
			stationNo: number;
		};
		return {
			name: raw.alias,
			description: raw.description,
			autoPrint: raw.autoPrint ? AutoPrintSwitch.Always : AutoPrintSwitch.None,
			dataSource: raw.dataSource,
			printerId: raw.printer,
			templateId: raw.template,
			stationNumber: raw.stationNo === 0 ? null : raw.stationNo,
		};
	});
}

@Component({
	selector: 'app-list-print-configuration',
	templateUrl: './list-print-configuration.component.html',
	styleUrl: './list-print-configuration.component.scss',
	animations: [trigger('expandCollapse', [transition(':enter', [style({ height: 0, opacity: 0, overflow: 'hidden' }), animate('250ms ease-out', style({ height: '*', opacity: 1 }))]), transition(':leave', [style({ overflow: 'hidden' }), animate('200ms ease-in', style({ height: 0, opacity: 0 }))])])],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ListPrintConfigurationComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public terminalConfig: { id: string | null; stationFilter: string | null };
	@Input() public initialPrintConfigurations: IPrintConfiguration[] = [];

	public expandedIndex: number | null = null;
	public stationOptions: Array<{ label: string; value: number }> = [];
	public addRowForm: FormGroup = this.fb.group({
		alias: [
			'',
			[
				Validators.required,
				Validators.maxLength(255),
				uniqueInArray(
					() => this.printConfigurations,
					(fg) => fg.get('alias')?.value,
				),
			],
		],
		description: ['', [Validators.required, Validators.maxLength(255)]],
	});

	public readonly dataSourceOptions = ['Group', 'Item'] as const;

	private destroy$ = new Subject<void>();
	public form = this.fb.group({
		printConfigurations: this.fb.array<FormGroup>([]),
	});

	public printerOptions: { id: string; name: string }[] = [];
	public templateOptions: { id: string; name: string }[] = [];

	constructor(
		private printService: PrintService,
		private docService: DocsService,
		private fb: FormBuilder,
	) {}

	public get printConfigurations(): FormArray<FormGroup> {
		return this.form.get('printConfigurations') as FormArray<FormGroup>;
	}

	public isGroupTouched(config: FormGroup): boolean {
		return config.dirty || config.touched;
	}

	public shouldShowError(control: AbstractControl | null): boolean {
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	public getFirstValidationErrorName(config: FormGroup, formControlName: string): string {
		const errors = config.get(formControlName)?.errors;

		return errors ? Object.keys(errors)[0] : '';
	}

	public getValidationErrorParams(config: FormGroup, formControlName: string): Record<string, number> {
		const control = config.get(formControlName);
		const errorName = this.getFirstValidationErrorName(config, formControlName);
		const error = control?.errors?.[errorName] as ValidationErrors | undefined;

		if (errorName === 'maxlength' && error?.['requiredLength']) {
			return { requiredLength: error['requiredLength'] as number };
		}

		if (errorName === 'min' && error?.['min'] !== undefined) {
			return { min: error['min'] as number };
		}

		return {};
	}

	public getRowLabel(config: FormGroup): string {
		return `(${config.get('alias')?.value ?? ''}) ${config.get('description')?.value ?? ''}`;
	}

	public compareStationNo(a: number, b: number): boolean {
		return a === b;
	}

	public toggleExpand(i: number): void {
		this.expandedIndex = this.expandedIndex !== i ? i : null;
	}

	public focusAliasInput(rowEl: HTMLElement): void {
		rowEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		const input = rowEl.querySelector<HTMLInputElement>('[id^="aliasInput_"]');
		if (input) {
			input.focus({ preventScroll: true });
			input.select();
		}
	}

	public onDrop(event: CdkDragDrop<FormGroup[]>): void {
		moveItemInArray(this.printConfigurations.controls, event.previousIndex, event.currentIndex);
		this.printConfigurations.markAsDirty();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildStationOptions(): void {
		const parsed = Number.parseInt(this.terminalConfig?.stationFilter ?? '', 10);
		const count = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
		this.stationOptions = Array.from({ length: count }, (_, i) => ({
			label: String(i + 1),
			value: i + 1,
		}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		const change = changes['terminalConfig'];
		if (change && change.currentValue?.stationFilter !== change.previousValue?.stationFilter) {
			this.buildStationOptions();
		}
	}

	ngOnInit(): void {
		this.buildStationOptions();
		this.initialPrintConfigurations.forEach((config) => this.printConfigurations.push(this.createPrintConfigurationForm(config)));

		this.printService
			.getPrinters()
			.pipe(takeUntil(this.destroy$))
			.subscribe((printers) => {
				this.printerOptions = printers.map((printer) => ({
					id: extractSelfId(printer.self),
					name: printer.alias,
				}));
			});

		this.docService
			.getTemplateList()
			.pipe(takeUntil(this.destroy$))
			.subscribe((templates) => {
				this.templateOptions = templates.result.map((template) => ({
					id: extractSelfId(template.self),
					name: template.name,
				}));
			});
	}

	public confirmAddRow(): void {
		if (this.addRowForm.invalid) return;
		const { alias, description } = this.addRowForm.value as {
			alias: string;
			description: string;
		};
		const fg = this.createPrintConfigurationForm({
			name: alias,
			description,
			autoPrint: AutoPrintSwitch.None,
			dataSource: DataSourceTypes.Item,
			printerId: '',
			templateId: '',
			stationNumber: null,
		});
		this.printConfigurations.push(fg);
		this.printConfigurations.markAsDirty();
		this.expandedIndex = this.printConfigurations.length - 1;
		this.addRowForm.reset({ alias: '', description: '' });
	}

	deletePrintConfiguration(index: number): void {
		if (this.expandedIndex === index) {
			this.expandedIndex = null;
		} else if (this.expandedIndex !== null && this.expandedIndex > index) {
			this.expandedIndex--;
		}

		this.printConfigurations.removeAt(index);
	}

	private createPrintConfigurationForm(config: IPrintConfiguration): FormGroup {
		const fg = this.fb.group({
			alias: [config.name, [Validators.required, Validators.maxLength(255)]],
			description: [config.description, [Validators.required, Validators.maxLength(255)]],
			stationNo: [config.stationNumber ?? 0],
			dataSource: [config.dataSource, Validators.required],
			autoPrint: [config.autoPrint === AutoPrintSwitch.Always],
			printer: [config.printerId, Validators.required],
			template: [config.templateId, Validators.required],
		});
		fg.get('alias').addValidators(
			uniqueInArray(
				() => this.printConfigurations,
				(fg2) => fg2.get('alias')?.value,
				() => this.printConfigurations.controls.indexOf(fg),
			),
		);
		fg.get('alias').updateValueAndValidity({ emitEvent: false });
		return fg;
	}

	public getPayload(): IPrintConfiguration[] {
		return buildTerminalConfigsPayload(this.printConfigurations.controls);
	}
}
